/**
 * As abas de filtro rápido da tela Administrativo: o que cada uma recorta, em que ordem abre e
 * que colunas fazem sentido dentro dela.
 *
 * Mora fora do componente pelo mesmo motivo do `quick-tabs-visibility.js` do Domínio: o
 * componente só é importável com DOM (puxa `angular` e o barril de gateways), e esta é a regra
 * que decide o que o usuário vê — precisa de teste de verdade, não de uma cópia da lógica
 * escrita no arquivo de teste.
 *
 * As chaves viram nome de evento (`aba.<key>`) no Mixpanel: renomear parte a série histórica.
 */

/**
 * Colunas que aparecem ou somem conforme a aba. As outras 17 valem em todas e não entram aqui —
 * listar o que é permanente só daria mais lugar para o HTML e esta tabela discordarem.
 */
export const COLUNAS_VARIAVEIS = [
    "cancelamento",
    "mmr",
    "boletoVencidoAposMmr",
    "churnMotivo",
    "dominio",
    "dominioUltimoDado",
    // As colunas do Conector NUCONT andam juntas: só fazem sentido na aba dele.
    "conector"
];

/**
 * O filtro de cada aba é escrito no MESMO objeto que o pop-over de filtros usa. A aba é atalho,
 * não filtro escondido: quem clicar em "Clientes ativos" e abrir o pop-over encontra Status
 * Interno "Ativo" marcado, e pode refinar por cima.
 *
 * `ativos` casa com "sem pedido em aberto", não com "sem pedido nenhum": contabilidade que pediu
 * rescisão e cumpriu o aviso prévio sem sair (`encerrado`) continua sendo cliente ativo. O
 * `sem-pedido` é traduzido para `null` pelo back, que é como o Mongo casa campo ausente.
 *
 * `churn` junta CHURN e NÃO VENDA porque é assim que o back já trata os dois — `getChurn()`
 * devolve o registro do churn para ambos.
 */
export const QUICK_TABS = [
    {
        // Primeira da fila por ser a visão sem recorte — mas não é onde a tela abre: quem entra
        // no Administrativo está atrás da carteira que paga, não do arquivo inteiro.
        key: "todos",
        label: "Todos",
        tooltip: "Toda a carteira Superlógica, em qualquer situação",
        countKey: "todos",
        filter: {},
        sort: { field: "createdAt", direction: -1 },
        columns: ["cancelamento", "mmr"]
    },
    {
        key: "ativos",
        label: "Clientes ativos",
        tooltip:
            "Clientes pagantes, com status ATIVO e sem pedido de cancelamento em aberto",
        countKey: "ativos",
        filter: {
            statusInternal: ["ATIVO"],
            rescission: ["sem-pedido", "encerrado"]
        },
        sort: { field: "createdAt", direction: -1 },
        columns: ["mmr", "dominio"]
    },
    {
        // Recorte dos ativos com o conector instalado. O dado mora em `automaticIntegrations`,
        // fora da collection da listagem — quem resolve isso é o back, no filtro `integration`.
        key: "dominio",
        label: "Clientes ativos com Domínio",
        tooltip:
            "Clientes ativos, sem pedido em aberto, com o conector do Domínio Ideen instalado",
        countKey: "comDominio",
        filter: {
            statusInternal: ["ATIVO"],
            rescission: ["sem-pedido", "encerrado"],
            integration: ["dominio"]
        },
        // Crescente de propósito: quem está parado há mais tempo vem primeiro, e quem nunca
        // recebeu nada vem antes de todo mundo (data ausente ordena antes de qualquer data no
        // MongoDB). A aba abre com a fila de trabalho pronta, não com a lista mais nova.
        sort: { field: "dominioLastDataAt", direction: 1 },
        columns: ["mmr", "dominioUltimoDado"]
    },
    {
        // O aplicativo próprio da NUCONT (`DominioNucont`), não o conector da Ideen da aba acima.
        // É a única aba fora da carteira Superlógica: quem tira a exigência de contrato é o back,
        // ao ver `dominio-nucont`. Abre só com as ATIVO: o conector já está nos clientes, e as
        // contabilidades de teste misturadas a eles atrapalhavam a leitura. Tirando o Status
        // Interno no pop-over, voltam quem tem o conector em qualquer status (o back conta a aba
        // com o mesmo recorte).
        key: "conector",
        label: "Conector NUCONT",
        tooltip:
            "Contabilidades ativas com o Conector NUCONT. Tire o filtro de status para ver também as de teste",
        countKey: "comConectorNucont",
        filter: { statusInternal: ["ATIVO"], integration: ["dominio-nucont"] },
        // A aba se reconhece só pelo conector: o Status Interno é o ponto de partida, não a
        // identidade dela. Quem tira o "Ativo" para ver as de teste continua na aba, com as
        // colunas do conector — senão a dica da própria aba levaria a uma tabela sem elas.
        casaPor: ["integration"],
        // O pior primeiro: nunca enviou sinal, sem sinal, com erro, fechado, ativo — e, dentro de
        // cada situação, quem falou há mais tempo. A aba abre com a fila de trabalho pronta.
        sort: { field: "conectorSituacao", direction: 1 },
        sortFallback: { field: "conectorUltimoSinal", direction: 1 },
        // MMR entra dentro do grupo do conector, logo depois do nome da contabilidade: a coluna
        // `mmr` de sempre fica depois de "Cliente desde", a oito colunas daqui.
        columns: ["conector"]
    },
    {
        key: "churn",
        label: "Clientes Churn",
        tooltip:
            "Quem saiu (CHURN) e quem nunca chegou a ser vendido (NÃO VENDA)",
        countKey: "churn",
        filter: { statusInternal: ["CHURN", "NÃO VENDA"] },
        sort: { field: "churnedAt", direction: -1 },
        // Segundo critério, pela data do PEDIDO de rescisão. Existe por duas razões: desempata
        // quem saiu no mesmo dia, e é o que segura a ordem enquanto a API publicada não
        // conhecer `churnedAt` — sem ele, a aba abriria na ordem natural da collection, que
        // não é ordem nenhuma para quem procura quem saiu por último.
        sortFallback: { field: "rescission", direction: -1 },
        // Sem MMR: quem saiu tem `superLogica.mrr` zerado, e uma coluna de zeros mente sobre o
        // tamanho que o cliente tinha.
        columns: ["cancelamento", "churnMotivo"]
    },
    {
        key: "pedido",
        label: "Clientes em aviso prévio",
        tooltip:
            "Contrato de pé, mas com pedido de rescisão correndo o aviso prévio",
        countKey: "comPedido",
        filter: { rescission: ["aguardando"] },
        sort: { field: "rescission", direction: -1 },
        columns: ["cancelamento", "mmr", "dominio"]
    },
    {
        // O `status` ATIVO ao lado do bloqueio não é redundância: bloqueado é cliente PAGANTE
        // com o acesso suspenso, e o ponto da aba é ir atrás da cobrança. Quem está bloqueado e
        // já INATIVO na Superlógica não tem mais contrato — o status interno segue BLOQUEADO
        // nos dois casos, então é a Superlógica que separa um do outro.
        key: "bloqueados",
        label: "Clientes bloqueados",
        tooltip:
            "Clientes pagantes com o acesso suspenso — BLOQUEADO e BLOQUEADO FINANCEIRO, quase sempre por boleto em aberto",
        countKey: "bloqueados",
        filter: {
            statusInternal: ["BLOQUEADO", "BLOQUEADO FINANCEIRO"],
            status: ["ATIVO"]
        },
        // Bloqueio é quase sempre boleto: a lista abre por quem tem mais em aberto.
        sort: { field: "expiredTickets", direction: -1 },
        columns: ["mmr", "boletoVencidoAposMmr"]
    }
];

/**
 * A lista de Trial é OUTRA tela — rota, contrato e tabela próprios —, mas para quem usa o
 * Administrativo ela é mais um recorte da mesma carteira. Aparece na mesma régua de abas e
 * navega em vez de filtrar; o `state` é o que marca essa diferença para quem monta a barra.
 */
export const TRIAL_TAB = {
    key: "trial",
    label: "Clientes Trial",
    tooltip: "Contabilidades em teste, ainda fora da carteira Superlógica",
    state: "nucont.accountings.trial"
};

/** As abas como a barra as mostra: os recortes da carteira e, no fim, o Trial. */
export const ALL_TABS = [...QUICK_TABS, TRIAL_TAB];


/** A aba em que a tela abre. */
export const ABA_PADRAO = "ativos";

export const findTab = (key) =>
    QUICK_TABS.find((tab) => tab.key === key) || null;

/**
 * Se a coluna aparece nesta aba.
 *
 * "Boleto Vencido" é caso especial e por isso tem duas chaves: `boletoVencidoAposMmr` é a
 * posição nova, ao lado do MMR, e ela existe SÓ na aba de bloqueados. Nas outras a coluna
 * continua onde sempre esteve, no fim da tabela — daí `boletoVencidoNoFim` ser a negação.
 */
export const isColumnVisible = ({ column, tabKey } = {}) => {
    if (column === "boletoVencidoNoFim")
        return !isColumnVisible({ column: "boletoVencidoAposMmr", tabKey });
    if (!COLUNAS_VARIAVEIS.includes(column)) return true;
    // Filtro que não corresponde a nenhuma aba cai na tabela da visão neutra. Mostrar TODAS as
    // colunas variáveis enchia a tela de "Sem motivo" e ainda mudava o Boleto Vencido de lugar
    // — a tabela trocava de forma sem que ninguém tivesse pedido esse recorte.
    const tab = findTab(tabKey) || findTab("todos");
    return tab.columns.includes(column);
};

/**
 * O filtro completo da aba, já no formato que `listCustomers` manda para o back.
 *
 * Parte do filtro zerado, e não do que está na tela: trocar de aba troca o recorte inteiro. O
 * que a aba não menciona (plano, score, período sem acesso) volta ao neutro de propósito —
 * carregar resto de filtro de outra aba é o caminho mais curto para uma lista vazia sem
 * explicação.
 */
export const tabFilter = (tabKey, filtroNeutro = {}) => {
    const tab = findTab(tabKey);
    return { ...filtroNeutro, ...(tab ? tab.filter : {}) };
};

/**
 * Qual aba corresponde ao filtro que está valendo, ou `null` para filtro personalizado.
 *
 * Derivar em vez de guardar é o que impede a aba de mentir: mexeu no pop-over e o recorte deixou
 * de ser o da aba, nenhuma fica acesa — que é a leitura correta.
 *
 * Só os campos que as abas usam entram na comparação; os demais são refinamento por cima e não
 * apagam a aba.
 */
const CAMPOS_DA_ABA = ["statusInternal", "rescission", "integration"];

/**
 * Filtro sem recorte nenhum: todo array vazio e todo número zerado.
 *
 * Existe por causa da aba "Todos", que é a única cuja promessa é "nada foi recortado". As
 * outras descrevem um recorte e aceitam refinamento por cima — quem está em "Clientes ativos"
 * e filtra por plano continua nos ativos. Já "Todos" acesa com a lista filtrada por Status
 * Superlógica ou por plano era mentira, e ainda mostrava o total da carteira no chip ao lado.
 */
const semRecorte = (filter = {}) =>
    Object.values(filter).every((valor) =>
        Array.isArray(valor) ? valor.length === 0 : !valor
    );

export const activeTabKey = (filter = {}) => {
    const iguais = (a = [], b = []) =>
        [...a].sort().join() === [...b].sort().join();

    const tab = QUICK_TABS.find((item) =>
        (item.casaPor || CAMPOS_DA_ABA).every((campo) =>
            iguais(filter[campo], item.filter[campo])
        )
    );
    if (!tab) return null;
    if (tab.key === "todos" && !semRecorte(filter)) return null;
    return tab.key;
};

/**
 * Os estados da coluna "Últ. dado Domínio".
 *
 * Sete dias porque a coleta do Domínio é semanal: passar disso quer dizer que um ciclo inteiro
 * falhou, não que o dado está um pouco atrasado. Trinta é o ponto em que o problema deixou de
 * ser incidente e virou integração morta — é onde a cor vira alerta.
 *
 * As faixas de filtro são mais finas que as cores (7, 15, 30, 60 dias): o time quer separar
 * "atrasou duas semanas" de "um mês" e "parou há dois meses", mas a cor continua dizendo só o
 * essencial — em dia, atenção, alerta.
 *
 * `nao-medido` não é filtrável: é a contabilidade que o cron noturno ainda não visitou. Existe
 * porque pintá-la de alerta junto com "sem registro" faria a carteira inteira aparecer vermelha
 * entre o deploy e a primeira rodada das 03:00 — e, pior, faria um cron quebrado parecer uma
 * integração quebrada.
 *
 * Os limites das faixas filtráveis valem no back, no filtro `dominioData`. Se mudarem aqui,
 * mudam lá.
 */
export const ESTADOS_DO_DOMINIO = [
    {
        key: "ate7",
        label: "Até 7 dias",
        tooltip: "Recebeu dado dentro do último ciclo de coleta",
        classe: "is-ok",
        filtravel: true
    },
    {
        key: "de7a15",
        label: "De 7 a 15 dias",
        tooltip: "Passou de um ciclo de coleta sem receber dado",
        classe: "is-atencao",
        filtravel: true
    },
    {
        key: "de15a30",
        label: "De 15 a 30 dias",
        tooltip: "Duas semanas ou mais sem dado novo",
        classe: "is-atencao",
        filtravel: true
    },
    {
        key: "de30a60",
        label: "De 30 a 60 dias",
        tooltip: "Mais de um mês sem dado novo — é o alerta",
        classe: "is-alerta",
        filtravel: true
    },
    {
        key: "mais60",
        label: "Mais de 60 dias",
        tooltip: "Mais de dois meses sem dado novo",
        classe: "is-alerta",
        filtravel: true
    },
    {
        key: "sem-dado",
        label: "Sem registro",
        tooltip:
            "Nenhum dado do Domínio registrado, nem pelo conector nem por importação automática",
        classe: "is-alerta",
        filtravel: true
    },
    {
        key: "nao-medido",
        label: "Não medido",
        tooltip: "O cálculo noturno ainda não passou por esta contabilidade",
        classe: "is-neutro",
        filtravel: false
    }
];

/** As faixas que viram checkbox no pop-over e chave de filtro no back. */
export const FAIXAS_DOMINIO = ESTADOS_DO_DOMINIO.filter(
    (estado) => estado.filtravel
);

const UM_DIA = 24 * 60 * 60 * 1000;

/**
 * Dias inteiros desde a data, para o texto da célula.
 *
 * Arredonda para CIMA porque o estado é decidido em dias fracionários: com `Math.floor`, uma
 * carga de 7 dias e 10 horas escrevia "há 7 d" e mesmo assim não vinha no filtro "Até 7 dias".
 * Para cima, o número exibido nunca promete uma faixa que a linha não ocupa — e some o "há 0 d"
 * de quem recebeu hoje de manhã.
 */
export const diasDesde = (data, agora = new Date()) => {
    if (!data) return null;
    return Math.ceil((agora.getTime() - new Date(data).getTime()) / UM_DIA);
};

/**
 * Em que estado a contabilidade está.
 *
 * Recebe o bloco inteiro, e não só a data, porque a distinção que importa está na presença do
 * bloco: sem bloco o cron não mediu; com bloco e sem data ele mediu e não há dado entrando.
 *
 * A comparação é em dias fracionários, e não no número arredondado que a célula mostra, para
 * casar exatamente com o intervalo que o back usa no filtro. Arredondar antes faria a linha
 * aparecer numa faixa e sumir quando o mesmo recorte fosse filtrado.
 */
export const estadoDoDominio = (dominioData, agora = new Date()) => {
    if (!dominioData) return "nao-medido";
    if (!dominioData.lastDataAt) return "sem-dado";
    const dias =
        (agora.getTime() - new Date(dominioData.lastDataAt).getTime()) / UM_DIA;
    if (dias <= 7) return "ate7";
    if (dias <= 15) return "de7a15";
    if (dias <= 30) return "de15a30";
    if (dias <= 60) return "de30a60";
    return "mais60";
};

export const estadoDoDominioInfo = (dominioData, agora = new Date()) =>
    ESTADOS_DO_DOMINIO.find(
        (estado) => estado.key === estadoDoDominio(dominioData, agora)
    );

/**
 * Como cada fonte do Domínio se chama na tela.
 *
 * O conector fala `accounting`/`tax`/`personnel`; o contador fala Contábil, Fiscal e Pessoal.
 * Traduzir num lugar só evita a tela mostrar o nome técnico em metade dos cantos.
 */
export const LABEL_DAS_FONTES = {
    accounting: "Contábil",
    tax: "Fiscal",
    personnel: "Pessoal"
};

/**
 * A linha do detalhe que resume a carteira conectada.
 *
 * Três leituras diferentes, e misturá-las já produziu contradição em tela:
 *
 * - sem empresa conectada, a contagem viraria "0 de 0", que não diz nada e esconde a causa real
 *   — o conector foi marcado e ninguém conectou empresa nenhuma;
 * - com a data vinda da importação, `companiesStale30d` conta pelo carimbo do conector, e as
 *   duas medem populações diferentes: dava "há 2 dias" ao lado de "12 de 12 paradas";
 * - só quando as duas saem do conector a contagem é comparável com a data da célula.
 */
export const resumoDaCarteira = (dados = {}) => {
    if (!dados.companiesConnected) return "Nenhuma empresa conectada ao Domínio";
    if (dados.lastDataOrigin === "importacao")
        return "Medido pelo histórico de importação — o conector ainda não carimbou nenhuma empresa";
    return `${dados.companiesStale30d} de ${dados.companiesConnected} empresas sem dado há mais de 30 dias`;
};

/**
 * Como cada fonte aparece no detalhe.
 *
 * Fonte sem nenhuma empresa habilitada não está parada — ninguém pediu dado dela. Dizer "sem
 * registro" ali gera chamado para um problema que não existe.
 */
export const estadoDaFonte = (fonte = {}, agora = new Date()) => {
    if (fonte.lastDataAt) return `há ${diasDesde(fonte.lastDataAt, agora)} dias`;
    if (!fonte.companiesActive) return "não habilitada";
    return "sem registro";
};

/**
 * Os valores que UMA opção do painel de filtros representa.
 *
 * Quase sempre é um só (`value`). Algumas opções juntam status que, para quem filtra, são a
 * mesma coisa — "Bloqueado" cobre também o BLOQUEADO FINANCEIRO — e levam a lista em
 * `values`. O back continua recebendo os valores crus.
 */
export const valoresDaOpcao = (opcao = {}) =>
    Array.isArray(opcao.values) ? opcao.values : [opcao.value];

/** A opção está marcada quando TODOS os seus valores estão no recorte. */
export const opcaoMarcada = (selecionados = [], opcao = {}) =>
    valoresDaOpcao(opcao).every((valor) => selecionados.includes(valor));

/**
 * Liga ou desliga a opção inteira no recorte, sem duplicar valor.
 *
 * Marcada: tira todos os valores dela. Desmarcada (ou marcada pela metade, quando um valor veio
 * de outro lugar): põe os que faltam.
 */
export const alternarOpcao = (selecionados = [], opcao = {}) => {
    const valores = valoresDaOpcao(opcao);
    if (opcaoMarcada(selecionados, opcao))
        return selecionados.filter((valor) => !valores.includes(valor));
    return [
        ...selecionados,
        ...valores.filter((valor) => !selecionados.includes(valor))
    ];
};

